import { useMemo } from "react";
import type { ActiveEntry } from "../lib/active";
import { fmtDeaths, fmtInt } from "../lib/format";
import { useApp } from "../lib/store";

/** "Where we stand": the present compared with the record, computed from the data. */
export function NowCard({ active }: { active: ActiveEntry[] }) {
  const stats = useApp((s) => s.stats);
  const meta = useApp((s) => s.meta);
  const c = useApp((s) => s.conflicts);
  const select = useApp((s) => s.select);
  const facts = useMemo(() => {
    if (!stats || !meta) return null;
    const at = (y: number) => y - stats.y0;
    const year = Math.floor(meta.now);
    const last = year - 1; // last complete year in the final UCDP release
    const d = (y: number) => stats.deaths[at(y)] ?? 0;
    const since89 = Array.from({ length: last - 1989 + 1 }, (_, k) => ({ y: 1989 + k, d: d(1989 + k) }));
    const rank = [...since89].sort((x, y) => y.d - x.d).findIndex((x) => x.y === last) + 1;
    const ww2End = 1945 + 8 / 12;
    const peak = since89.reduce((m, x) => (x.d > m.d ? x : m), since89[0]);
    return {
      year, last, rank, peak, conflictsNow: stats.active[at(year)], warsLast: stats.wars[at(last)],
      deathsLast: d(last), deathsNow: d(year), sinceWW2: Math.floor(meta.now - ww2End),
      ww2Yearly: d(1943), series: since89.concat([{ y: year, d: d(year) }]),
    };
  }, [stats, meta]);
  if (!facts || !c) return null;
  const deadliest = [...active].filter((e) => e.deathsYear != null && e.deathsYear > 0).sort((a, b) => (b.deathsYear ?? 0) - (a.deathsYear ?? 0)).slice(0, 4);
  const max = Math.max(...facts.series.map((x) => x.d));
  return (
    <section className="now-card" aria-label="Where we stand today">
      <h2>Where we stand</h2>
      <div className="now-grid">
        <div><strong>{fmtInt(facts.warsLast)}</strong><span>wars with ≥1,000 deaths in {facts.last}</span></div>
        <div><strong>{fmtInt(active.length)}</strong><span>conflicts going on now, including smaller ones</span></div>
        <div><strong>{fmtDeaths(facts.deathsLast)}</strong><span>deaths in organised violence in {facts.last}</span></div>
      </div>
      <p>
        {facts.last} was the {facts.rank === 1 ? "" : `${ordinalWord(facts.rank)} `}deadliest year since 1989 in UCDP's records
        {facts.peak.y !== facts.last ? ` (the peak: ${facts.peak.y}, ${fmtDeaths(facts.peak.d)})` : ""}. World War II ended{" "}
        {facts.sinceWW2} years ago; in 1943 alone, battles killed about {fmtDeaths(facts.ww2Yearly)} people.
      </p>
      <div className="spark" role="img" aria-label={`Deaths per year since 1989; ${facts.last}: ${fmtDeaths(facts.deathsLast)}`}>
        {facts.series.map((x) => (
          <span key={x.y} title={`${x.y}: ${fmtDeaths(x.d)}${x.y === facts.year ? " (January–September, provisional)" : ""}`}
            className={x.y === facts.year ? "partial" : ""} style={{ height: `${Math.max(2, (x.d / max) * 100)}%` }} />
        ))}
      </div>
      <div className="spark-axis"><span>1989</span><span>{facts.year}</span></div>
      {deadliest.length > 0 && (
        <>
          <h3>Deadliest in {facts.year} so far</h3>
          <ul className="deadliest">
            {deadliest.map((e) => (
              <li key={e.i}><button onClick={() => select(e.i, { fly: true })}>{c.name[e.i]}</button> <span>{fmtDeaths(e.deathsYear)}</span></li>
            ))}
          </ul>
        </>
      )}
      <p className="fineprint">{facts.year} figures are UCDP's provisional monthly data (January–September); earlier years are final releases.</p>
    </section>
  );
}

export function ordinalWord(n: number): string {
  const words = ["", "first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth"];
  if (words[n]) return words[n];
  const s = n % 100 >= 11 && n % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${s}`;
}
